import CustomSelect from '~/components/Shared/CustomSelect/CustomSelect';
import { InputBar } from '~/ui';
import { useFormik } from 'formik';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { IUsersFilters } from '~/utils/types/userFilterTypes';
import { validate } from '~/utils/validateFeedbackFilter';
import styles from './FeedbackFilters.module.scss';
import { useEffect, useState } from 'react';
import { FilterButtons } from '../FiltersButtons/FilterButtons';
import { getCompaniesModuleOptions } from '~/utils/getCompaniesModuleOptions';
import { CustomDatePicker } from '~/components/Shared/CustomDatePicker/CustomDatePicker';

interface IFormik {
  dates: {
    start_date: Dayjs | null;
    end_date: Dayjs | null;
  };

  category_ids: { id: number; name: string }[] | [];
  feedback: {
    condition: { id: number; name: string; display_name: string };
    average: string;
  };
  announcement_organizations: { id: number; name: string; full_name: string }[];
}

interface IOrderProps {
  selectedUsers: number[];
  onClick: (filters: IUsersFilters) => void;
  setIsStatusUsersModalOpen: () => void;
  isUsersLoading: boolean;
}

export const FeedbackFilters = ({
  onClick,
  selectedUsers,
  setIsStatusUsersModalOpen,
  isUsersLoading,
}: IOrderProps) => {
  const [isAnyFieldFilled, setIsAnyFieldFilled] = useState(false);
  const toastError = useToastError();

  const { data: companies } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/user-company');
      return response.data;
    },
    queryKey: ['user-company'],
    onError: error => {
      toastError(error);
    },
  });

  const companyModules = getCompaniesModuleOptions(companies?.companies);

  const { data: feedbackCategories } = useQuery({
    queryFn: async () => {
      const response = await instance.get('admin/feedback-categories');
      return response.data;
    },
    queryKey: ['admin/feedback-categories'],
    onError: error => {
      toastError(error);
    },
  });

  const getModuleOptions = (data: {
    [key: string]: {
      id: number;
      name: string;
      status: string;
    };
  }) => {
    if (!data) return [];
    const modulesOptions = Object.values(data);
    return modulesOptions;
  };

  const modulesOptions = getModuleOptions(feedbackCategories?.categories);

  const formik = useFormik<IFormik>({
    initialValues: {
      dates: {
        start_date: null,
        end_date: null,
      },
      category_ids: [],
      feedback: {
        condition: { id: 0, name: '', display_name: '' },
        average: '',
      },
      announcement_organizations: [],
    },
    validate,
    onSubmit: values => {
      const categoriesIds = values.category_ids.map(item => item.id);
      const company_name = values.announcement_organizations.map(
        item => item.name
      );
      const filters = {
        feedbacks: {
          ...(values.dates.start_date &&
            values.dates.end_date && {
              dates: {
                start_date: dayjs(values.dates.start_date)
                  .startOf('day')
                  .format('YYYY-MM-DD HH:mm:ss'),
                end_date: dayjs(values.dates.end_date)
                  .endOf('day')
                  .format('YYYY-MM-DD HH:mm:ss'),
              },
            }),
          ...(!!values.category_ids.length && {
            category_ids: categoriesIds,
          }),
          ...(values.feedback.condition &&
            values.feedback.average && {
              condition: values.feedback.condition.name,
              average: values.feedback.average,
            }),
        },
        ...(values.announcement_organizations.length !== 0
          ? {
              companies: {
                names: company_name,
              },
            }
          : {}),
      };
      onClick(filters);
    },
  });

  const handleResetForm = () => {
    formik.resetForm();
    onClick({});
  };

  useEffect(() => {
    const isFilled =
      !!formik.values.dates.start_date ||
      !!formik.values.dates.end_date ||
      !!formik.values.category_ids.length ||
      !!formik.values.feedback.average ||
      !!formik.values.feedback.condition.name ||
      !!formik.values.announcement_organizations.length;

    setIsAnyFieldFilled(isFilled);
  }, [formik.values]);

  return (
    <div className={styles.orderContainer}>
      <form onSubmit={formik.handleSubmit} className={styles.formWrapper}>
        <div className={styles.companyWrapper}>
          <CustomSelect
            label={t('company')}
            options={companyModules}
            value={formik.values.announcement_organizations}
            onChange={value => {
              formik.setFieldValue('announcement_organizations', value);
            }}
            placeholder={t('validate_organizations')}
            disabled={isUsersLoading}
            multiselect
            className={styles.companySelect}
          />
          <div className={styles.dateWrapper}>
            <CustomDatePicker
              title={t('feedback_period')}
              value={[
                formik.values.dates.start_date,
                formik.values.dates.end_date,
              ]}
              placeholder={[t('date'), t('date')]}
              onChange={(name, value) => {
                if (name === 'start_date') {
                  formik.setFieldValue('dates.start_date', value);
                } else {
                  formik.setFieldValue('dates.end_date', value);
                }
              }}
            />
          </div>
        </div>
        <div className={styles.operators}>
          <div className={styles.operatorWrapper}>
            <CustomSelect
              label={t('feedback_category')}
              value={formik.values.category_ids}
              className={styles.topSelect}
              options={modulesOptions}
              placeholder={t('select_categories')}
              onChange={value => formik.setFieldValue('category_ids', value)}
              multiselect
              error={
                formik.touched.category_ids
                  ? (formik.errors.category_ids as string)
                  : undefined
              }
            />
          </div>
          <div className={styles.operatorWrapper}>
            <span className={styles.operatorsLabel}>
              {t('average_of_rating')}
            </span>
            <div className={styles.operatorLine}>
              <CustomSelect
                value={formik.values.feedback.condition}
                options={[
                  { id: 1, display_name: `${t('more')}`, name: '>' },
                  { id: 2, display_name: `${t('less')}`, name: '<' },
                  { id: 3, display_name: `${t('equal')}`, name: '=' },
                ]}
                placeholder={t('select_operator')}
                onChange={value =>
                  formik.setFieldValue('feedback.condition', value)
                }
                error={
                  formik.touched.feedback?.condition?.name
                    ? (formik.errors.feedback?.condition?.name as string)
                    : undefined
                }
              />
              <InputBar
                value={formik.values.feedback.average}
                name="feedback.average"
                onChange={formik.handleChange}
                classNameInputWrapper={styles.input}
                placeholder={t('enter_the_number')}
                label=""
                errors={formik.errors.feedback?.average}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.feedback?.average &&
                  Boolean(formik.errors.feedback?.average)
                }
              />
            </div>
          </div>
        </div>
        <FilterButtons
          setIsModalOpen={setIsStatusUsersModalOpen}
          isAnyFieldFilled={isAnyFieldFilled}
          selectedUsers={selectedUsers}
          resetForm={handleResetForm}
          isLoading={isUsersLoading}
        />
      </form>
    </div>
  );
};
